import { useEffect, useState, lazy, Suspense } from "react";
import "./App.css";
import { useNavigate, useRoutes, useSearchParams } from "react-router-dom";
import { SchedItem } from "../api/schedule.js";
import { FoodItem } from "../api/food.js";
import * as closest from "closest-match";
import * as nsfw from "nsfwjs";
import * as util from "../api/util.js";
import { initializeApp } from "firebase/app";
import { getMessaging } from "firebase/messaging";
import firebaseConfig from "../fbc.json" with { type: "json" };

const NotifsDisp = lazy(() => import("./NotifsDisp.jsx"));
const DefCont = lazy(() => import("./DefCont.jsx"));
const Home = lazy(() => import("./Home.jsx"));
const Imgs = lazy(() => import("./Imgs.jsx"));
const SchedPlain = lazy(() => import("./SchedPlain.jsx"));
const Sched = lazy(() => import("./Sched.jsx"));
const SignIn = lazy(() => import("./SignIn.jsx"));
const SignUp = lazy(() => import("./SignUp.jsx"));
const SignOut = lazy(() => import("./SignOut.jsx"));
const Profile = lazy(() => import("./Profile.jsx"));
const Manage = lazy(() => import("./Manage.jsx"));
const ManageSched = lazy(() => import("./ManageSched.jsx"));
const View = lazy(() => import("./View.jsx"));
const Anns = lazy(() => import("./Anns.jsx"));
const Map = lazy(() => import("./Map.jsx"));
const PageNotFound = lazy(() => import("./PageNoutFound.jsx"));
const Create = lazy(() => import("./Create.jsx"));
const User = lazy(() => import("./User.jsx"));
const Food = lazy(() => import("./Food.jsx"));
const Leaderboard = lazy(() => import("./Leaderboard.jsx"));
const Notifs = lazy(() => import("./Notifs.jsx"));
const NotifRequestModal = lazy(() => import("./NotifRequestModal.jsx"));
const MustSignIn = lazy(() => import("./MustSignIn.jsx"));
const Settings = lazy(() => import("./Settings.jsx"));

function Susp({ l, e: E }) {
  return <Suspense fallback={<div>{`Loading ${l}...`}</div>}><E/></Suspense>
}
const homeSusp = <Susp l="home" e={Home}/>
const routes = [
  { path: "/", element: homeSusp, unlisted: false },
  { path: "/home", element: homeSusp, unlisted: false },
  { path: "/index", element: homeSusp, unlisted: false },
  { path: "/sched", element: <Susp l="schedule" e={Sched}/>, unlisted: false },
  { path: "/anns", element: <Susp l="announcements" e={Anns}/>, unlisted: false },
  { path: "/map", element: <Susp l="map" e={Map}/>, unlisted: false },
  { path: "/signin", element: <Susp l="signin form" e={SignIn}/>, unlisted: false },
  { path: "/signup", element: <Susp l="signup form" e={SignUp}/>, unlisted: false },
  { path: "/signout", element: <Susp l="signout" e={SignOut}/>, unlisted: false },
  { path: "/manage", element: <Susp l="user manager" e={Manage}/>, unlisted: true },
  { path: "/verify", element: <Susp l="verification" e={Verify}/>, unlisted: true },
  { path: "/profile", element: <Susp l="profile" e={Profile}/>, unlisted: false },
  { path: "/verified", element: <Susp l="verification results" e={Verified}/>, unlisted: true },
  { path: "/view", element: <Susp l="schedule item viewer" e={View}/>, unlisted: true },
  { path: "/user", element: <Susp l="user viewer" e={User}/>, unlisted: true },
  { path: "/create", element: <Susp l="item creator" e={Create}/>, unlisted: false },
  { path: "/reset", element: <Susp l="reset" e={Reset}/>, unlisted: false },
  { path: "/managesched", element: <Susp l="schedule manager" e={ManageSched}/>, unlisted: true },
  { path: "/food", element: <Susp l="food" e={Food}/>, unlisted: false },
  { path: "/imgs", element: <Susp l="images" e={Imgs}/>, unlisted: false },
  { path: "/board", element: <Susp l="leaderboard" e={Leaderboard}/>, unlisted: false },
  { path: "/notifcenter", element: <Susp l="Loading notification center" e={Notifs}/>, unlisted: true },
  { path: "/schedplain", element: <Susp l="loading plaintext schedule" e={SchedPlain}/>, unlisted: false },
  { path: "/mustsignin", element: <Susp l="signin dialog" e={MustSignIn}/>, unlisted: true },
  { path: "/settings", element: <Susp l="settings" e={Settings}/>, unlisted: false },
  { path: "*", element: <Susp l="404 page" e={PageNotFound}/>, unlisted: true }
];

const rankings = ["newcomer", "rookie", "seasoned", "pro", "epic", "extrodinary", "wise", "ancient", "superhuman", "mythical", "urban legend"];
const foods = [new FoodItem(0, "cracker barrel", [["h", "1"], ["e", "2"], ["l", "3"], ["l", "4"], ["o", "5"]])];

// errors
class HTTPErr extends Error { constructor(response) { super(`HTTP error: ${response.status}`); this.name = "HTTPError"; } }
class httpErr extends Error { constructor(response, data) { super(`HTTP error: ${response.status} ${data.message}`); this.name = "HTTPError"; } }
function codeFmt(r) { return `Response code ${r.status} (${r.statusText})`; }
class NoFulfill extends Error { constructor(data, res) { super(`The server could not fulfill your request:\n${data.message}\n${codeFmt(res)}`); } }
class API {
  constructor() {
    this.root = "https://conclave-website-server.onrender.com/api/";
  }
  sendGet(path) {
    return fetch(this.root + path);
  }
  sendPost(path, body = null) {
    const p = this.root + path;
    if(body) return fetch(p, {
      ...this.headers(), body: JSON.stringify(body)
    });
    return fetch(p, this.headers());
  }
  headers() {
    return { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" } };
  }
}
const api = new API();
const app = initializeApp(firebaseConfig);
const messaging = getMessaging(app);
window.addEventListener("error", (e) => alert(`msg: ${e.message} (file ${e.filename} at ln ${e.lineno})`));
function setUserObj(u) {
  if(!u) return;
  sessionStorage.setItem("user", JSON.stringify(u));
}
function getUserObj() {
  try {
    const raw = sessionStorage.getItem("user");
    if(!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed == "object" ? parsed : {};
  } catch {
    return {};
  }
}
function uExists() {
  return Object.keys(getUserObj()).length > 0;
}
var user = getUserObj();
(() => {
  const _ = sessionStorage.getItem("user");
  if(!_ || _ == null || _ == "null" || _ == undefined || _ == "undefined") { setUserObj({}); user = {}; }
})();

function Logo() {
  const n = useNavigate();
  return <img src="/placeholder.png" onClick={() => n("/")}/>
}

function isIn() {
  return parseInt(sessionStorage.getItem("in") ?? "0");
}

async function signIn() {
  if(isIn()) return;
  await (async () => {
    try {
      const r = await api.sendPost("isexpr");
      if(!r.ok) throw new Error(`Failed to verify:\n${codeFmt(r)}`);
      const res = await api.sendPost("verify");
      if(!res.ok) throw new Error(`Failed to verify:\n${codeFmt(res)}`);
      const data = await res.json();
      if(data.result) sessionStorage.setItem("in", "1");
      user = await getUser();
      setUserObj(user);
    } catch(e) { console.error(e); }
  })();
}

async function getUser() {
  try {
    const res = await api.sendPost("getuser");
    if(!res.ok) throw new Error(`Failed to get user:\n${codeFmt(res)}`);
    return (await res.json()).u;
  } catch(e) { console.error(e); }
}
async function getUUID() {
  return (await getUser()).user_id;
}
async function notif(title, body) {
  try {
    const res = await api.sendPost("notif", { title, body });
    if(!res.ok) throw new HTTPErr(res);
  } catch(e) {
    console.error(e);
  }
}

function getClose(input, items, min) {
  return items.filter(i => closest.distance(input, i) <= min);
}
function sortClose(input, items) {
  return items.sort((a, b) => closest.distance(a, input) - closest.distance(b, input));
}

async function reqIn(n) {
  if(!(parseInt(sessionStorage.getItem("in" ?? "0")) || !(await (await api.sendPost("isexpr")).json()).x)) n("/index");
}

const device = window.matchMedia("(display-mode: standalone)").matches ? "mobile" : "pc";

export { httpErr, foods, rankings, adminRestrict, NoFulfill, codeFmt, getLatestSchedule, HTTPErr, IcoTx, pullImgs, tagImgs, getUserObj, uExists, closest, api, routes, messaging, util, setUserObj, device };

// function StickyBar() {
//   const [dayCount, setDayC] = useState("");
//   setInterval(() => {
//     const d1 = new Date();
//     const d2 = new Date("2026-04-10");
//     const dif = Math.abs(d2.getTime() - d1.getTime());
//     const s = Math.floor(dif / 1000);
//     const m = Math.floor(s / 60);
//     const h = Math.floor(m / 60);
//     const d = Math.floor(h / 24);
//     const rs = s % 60;
//     const rm = m % 60;
//     const rh = h % 24;
//     setDayC(`Days Till Conclave: ${d} days, ${rh} hours, ${rm} minutes, ${rs} seconds`);
//   }, 1);
//   return <div className="sticky-bar">Conclave Website Preview - {dayCount}</div>
// }

async function pullImgs(folderId) {
  try {
    const res = await api.sendPost("pullimgs", { id: folderId });
    const data = await res.json();
    return data.imgs;
  } catch(e) {
    console.error(e);
    return null;
  }
}
async function tagImgs(imgs) {
  const out = [];
  const model = await nsfw.load("MobileNetV2");
  for(const i of imgs) {
    const x = new Image();
    x.src = i;
    x.onload = async () => {
      const tags = await model.classify(x);
      const tag = Object.entries(tags).sort((a, b) => a[1] - b[1])[0][1].className;
      out.push({ url: i, tag });
    }
  }
  return out;
}

async function getLatestSchedule() {
  try {
    const res = await api.sendGet("scheditems");
    if(!res.ok) throw new HTTPErr(res);
    const data = await res.json();
    return data.items.map(i => new SchedItem(i.name, i.desc, i.head, i.loc, i.day, i.start, i.end, i.xp));
  } catch(e) {
    console.error(e);
  }
}

function IcoTx({ ico: Icon, children }) {
  return <p style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
    <Icon/>
    <span>{children}</span>
  </p>
}

async function isAdmin() {
  if(!isIn()) return false;
  try {
    const res = await api.sendPost("isuseradmin");
    if(!res.ok) throw new Error(`Failed to validate:\n${codeFmt(res)}`);
    const data = await res.json();
    return data.result;
  } catch(e) { console.error(e); }
}

function adminRestrict(nav) {
  if(!isAdmin()) nav("/index");
}

function Verified() {
  const [params] = useSearchParams();
  const success = params.get("success") ?? false;
  return success == "true" ? <div>
    <DefCont title="Verification Success"/>
    <p>Verification successful.</p>
  </div> : <div>
    <DefCont title="Verification Failed"/>
    <p>Verification failed.</p>
  </div>
}

function Verify() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const n = useNavigate();
  useEffect(() => {
    if(!token) throw new Error("Missing token");
    const verify = async () => {
      try {
        const res = await api.sendPost("verifytk", { token });
        if(!res.ok) throw new HTTPErr(res);
        const data = await res.json();
        n(`/verified?success=${data.result}`);
      } catch(e) {
        console.error(e);
      }
    }
    verify();
  }, []);
}

function Reset() {
  const [params] = useSearchParams();
  const tk = params.get("token");
  const [isValid, setIsValid] = useState(false);
  const n = useNavigate();
  useEffect(() => {
    (async () => {
      try {
        const res = await api.sendPost("verifytk", { token: tk });
        if(!res.ok) throw new HTTPErr(res);
        const data = await res.json();
        setIsValid(data.result);
      } catch(e) {
        console.error(e);
      }
    })();
  }, []);
  const submit = async (e) => {
    e.preventDefault();
    const pass = e.currentTarget.pass.value;
    try {
      const res = await api.sendPost("setpass", { pass });
      if(!res.ok) throw new HTTPErr(res);
      n("/index");
    } catch(e) {
      console.error(e);
    }
  }
  return isValid ? <form>
      <h3>Enter Your New Password</h3>
      <input type="text" placeholder="Some new password..." id="pass" required/>
      <button type="submit" onClick={submit}>Finish</button>
    </form> : <p>Token verification failed. Check console for details.</p>
}

function App() {
  // const [status, setStatus] = useState("Waiting to connect to server...");
  // const [users, setUsers] = useState("Fetching registered users...");
  const routesEl = useRoutes(routes);
  if(!isIn()) signIn();
  // useEffect(() => {
  //   const setupDatabase = async () => {
  //     try {
  //       const response = await fetch("http://localhost:3001/api/status");
  //       const data = await response.json();
  //       if(data.success) {
  //         setStatus("Database setup successful! " + data.message + " (time: " + data.time + ")");
  //       } else {
  //         setStatus("Database setup failed: " + data.message);
  //       }
  //     } catch(e) {
  //       setStatus("Failed to connect to the server. Is your Node.js server running?");
  //       console.error(e);
  //     }
  //   }
  //   setupDatabase();
  // }, []);
  // useEffect(() => {
  //   const fetchUsers = async () => {
  //     try {
  //       const response = await fetch("http://localhost:3001/api/users");
  //       const data = await response.json();
  //       if(data.success) {
  //         setUsers("User fetch successfull! " + data.message + " - users: " + JSON.stringify(data.users));
  //       } else {
  //         setUsers("User fetch failed: " + data.message);
  //       }
  //     } catch(e) {
  //       setUsers("Failed to fetch users.");
  //       console.error(e);
  //     }
  //   }
  //   fetchUsers();
  // }, []);
  return <div>
    <NotifsDisp/>
    <NotifRequestModal/>
    {routesEl}
  </div>
}

export default App
